import { useEffect, type ReactNode } from 'react';
import { useAuthStore } from '@/store/auth';
import { useProjectStore } from '@/store/project';
import { useOrgStore } from '@/store/org';

/**
 * Загружает список проектов пользователя после входа и очищает всё при выходе
 * (смена пользователя на общем устройстве не должна тащить чужой проект).
 */
export function ProjectProvider({ children }: { children: ReactNode }) {
  const userId = useAuthStore((s) => s.user?.id ?? null);
  const authLoading = useAuthStore((s) => s.isLoading);
  const load = useProjectStore((s) => s.load);
  const reset = useProjectStore((s) => s.reset);
  const loadOrgs = useOrgStore((s) => s.load);
  const resetOrgs = useOrgStore((s) => s.reset);

  useEffect(() => {
    // Пока вход не определился, ничего не стираем: сохранённый проект нужен
    // сразу после того, как сессия восстановится.
    if (authLoading) return;
    if (userId) { void load(); void loadOrgs(); }
    else { reset(); resetOrgs(); }
  }, [userId, authLoading, load, reset, loadOrgs, resetOrgs]);

  return <>{children}</>;
}

/** Активный проект: id для CRM и номер кабинета Chatwoot. */
export function useProject() {
  const projects = useProjectStore((s) => s.projects);
  const activeProjectId = useProjectStore((s) => s.activeProjectId);
  const status = useProjectStore((s) => s.status);
  const error = useProjectStore((s) => s.error);
  const setActive = useProjectStore((s) => s.setActive);
  const reload = useProjectStore((s) => s.load);
  const active = projects.find((p) => p.id === activeProjectId) ?? null;
  return {
    projects,
    status,
    error,
    active,
    projectId: activeProjectId,
    chatwootAccountId: active?.chatwoot_account_id ?? null,
    setActive,
    reload,
  };
}
